import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { api, parsePaginatedResponse } from '../../lib/api';
import { useAuthStore } from '../../store/auth';

interface Campaign {
    id: string;
    name: string;
}

interface Gift {
    id: string;
    code?: string;
    name: string;
    metadata?: {
        type?: string;
    };
}

interface Player {
    id: string;
    campaign_id?: string;
    campaign_name?: string | null;
    full_name?: string | null;
    email?: string | null;
    phone?: string | null;
    identity_type?: string;
    identity_value?: string | null;
    status?: 'active' | 'blocked';
    blocked_at?: string | null;
    blocked_reason?: string | null;
    created_at?: string;
    first_seen_at?: string | null;
    last_seen_at?: string | null;
    total_plays?: number;
    total_wins?: number;
    last_played_at?: string | null;
    last_reward_at?: string | null;
    reward_names?: string[];
    won_gifts?: { gift_id: string; gift_name: string; win_count: number }[];
    ip_hash?: string | null;
    shared_ip_count?: number | null; // số player khác cùng IP trong campaign
    is_ip_blocked?: boolean;
}

interface RewardDetail {
    id: string;
    gift_name?: string | null;
    gift_code?: string | null;
    gift_type?: string | null;
    status?: string;
    reward_code?: string | null;
    assigned_at?: string | null;
    claimed_at?: string | null;
    created_at?: string | null;
}

interface PlaySessionDetail {
    id: string;
    campaign_name?: string | null;
    status?: string;
    started_at?: string | null;
    finished_at?: string | null;
    rewards?: RewardDetail[];
}

interface PlayerDetails extends Player {
    external_customer_id?: string | null;
    fingerprint_hash?: string | null;
    form_data?: Record<string, unknown>;
    consent_given?: boolean;
    total_plays: number;
    total_wins: number;
    rewards?: RewardDetail[];
    play_sessions?: PlaySessionDetail[];
}

interface Filters {
    campaignId: string;
    query: string;
    status: 'all' | 'active' | 'blocked';
    rewardStatus: 'all' | 'winners' | 'non_winners';
    giftType: string;
    giftId: string;
    startDate: string;
    endDate: string;
}

const PAGE_SIZE = 20;

const initialFilters = (campaignId?: string): Filters => ({
    campaignId: campaignId || '',
    query: '',
    status: 'all',
    rewardStatus: 'all',
    giftType: '',
    giftId: '',
    startDate: '',
    endDate: '',
});

function formatDate(value?: string | null): string {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function getStatusLabel(status?: string): string {
    if (status === 'blocked') return 'Đã chặn';
    return 'Hoạt động';
}

function getGiftTypeLabel(type?: string | null): string {
    if (!type) return 'Không rõ';
    if (type === 'voucher') return 'Voucher';
    if (type === 'physical') return 'Vật lý';
    if (type === 'gift') return 'Quà tặng';
    return type;
}

export default function Players() {
    const [searchParams] = useSearchParams();
    const urlCampaignId = searchParams.get('campaignId') || undefined;
    const { user } = useAuthStore();
    const tenantId = localStorage.getItem('impersonated_tenant_id') || user?.tenant_id || '';

    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [gifts, setGifts] = useState<Gift[]>([]);
    const [players, setPlayers] = useState<Player[]>([]);
    const [filters, setFilters] = useState<Filters>(() => initialFilters(urlCampaignId));
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [detail, setDetail] = useState<PlayerDetails | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [blockingIpHash, setBlockingIpHash] = useState<string | null>(null);

    useEffect(() => {
        setFilters((current) => {
            if ((urlCampaignId || '') === current.campaignId) return current;
            return { ...current, campaignId: urlCampaignId || '' };
        });
        setPage(1);
    }, [urlCampaignId]);

    const rewardGifts = useMemo(() => {
        return gifts.filter((gift) => gift.metadata?.type !== 'none');
    }, [gifts]);

    const giftTypes = useMemo(() => {
        return Array.from(new Set(rewardGifts.map((gift) => gift.metadata?.type).filter((type): type is string => Boolean(type))));
    }, [rewardGifts]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const updateFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
        setFilters((current) => ({ ...current, [key]: value }));
        setPage(1);
    };

    const loadLookups = async () => {
        if (!tenantId) return;
        const [campaignResponse, giftResponse] = await Promise.all([
            api.getCampaigns(tenantId, 1, 100),
            api.getGifts(tenantId, filters.campaignId || undefined, 1, 500),
        ]);
        setCampaigns(parsePaginatedResponse<Campaign>(campaignResponse).items);
        setGifts(parsePaginatedResponse<Gift>(giftResponse).items);
    };

    const searchPlayers = async () => {
        if (!tenantId) return;
        setLoading(true);
        setError('');
        try {
            const response = await api.searchPlayers(
                tenantId,
                filters.campaignId || undefined,
                filters.query || undefined,
                page,
                PAGE_SIZE,
                {
                    status: filters.status,
                    rewardStatus: filters.rewardStatus,
                    giftType: filters.giftType || undefined,
                    giftId: filters.giftId || undefined,
                    startDate: filters.startDate || undefined,
                    endDate: filters.endDate || undefined,
                },
            );
            const parsed = parsePaginatedResponse<Player>(response);
            setPlayers(parsed.items);
            setTotal(parsed.total);
            setSelectedIds((current) => current.filter((id) => parsed.items.some((player) => player.id === id)));
        } catch (err) {
            console.error('Failed to search players:', err);
            setError('Không tải được danh sách người chơi.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadLookups().catch((err) => {
            console.error('Failed to load player filters:', err);
            setError('Không tải được dữ liệu bộ lọc.');
        });
    }, [tenantId, filters.campaignId]);

    useEffect(() => {
        searchPlayers();
    }, [tenantId, page, filters]);

    const allVisibleSelected = players.length > 0 && players.every((player) => selectedIds.includes(player.id));

    const toggleAllVisible = () => {
        if (allVisibleSelected) {
            setSelectedIds((current) => current.filter((id) => !players.some((player) => player.id === id)));
            return;
        }
        setSelectedIds((current) => Array.from(new Set([...current, ...players.map((player) => player.id)])));
    };

    const togglePlayer = (id: string) => {
        setSelectedIds((current) => (
            current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
        ));
    };

    const runBulkAction = async (action: 'block' | 'unblock' | 'delete') => {
        if (selectedIds.length === 0 || !tenantId) return;
        setSaving(true);
        setError('');
        try {
            if (action === 'block') {
                const reason = window.prompt('Lý do chặn người chơi', 'Vi phạm điều kiện tham gia') || undefined;
                await api.blockPlayers(tenantId, selectedIds, reason);
            }
            if (action === 'unblock') {
                await api.unblockPlayers(tenantId, selectedIds);
            }
            if (action === 'delete') {
                const ok = window.confirm('Xóa người chơi đã chọn khỏi danh sách quản trị?');
                if (!ok) return;
                await api.deletePlayers(tenantId, selectedIds);
            }
            setSelectedIds([]);
            await searchPlayers();
        } catch (err) {
            console.error('Failed to update players:', err);
            setError('Không thực hiện được thao tác với người chơi đã chọn.');
        } finally {
            setSaving(false);
        }
    };

    const openDetails = async (playerId: string) => {
        if (!tenantId) return;
        setDetailLoading(true);
        setError('');
        try {
            const res  = await api.getPlayerDetails(playerId, tenantId);
            // Backend trả { data: {...} } — unwrap
            setDetail(res?.data ?? res);
        } catch (err) {
            console.error('Failed to load player details:', err);
            setError('Không tải được chi tiết người chơi.');
        } finally {
            setDetailLoading(false);
        }

    };

    const resetFilters = () => {
        setFilters(initialFilters(urlCampaignId));
        setPage(1);
    };

    // Block IP từ player row — admin không cần biết raw IP
    const blockIpFromPlayer = async (player: Player | PlayerDetails) => {
        if (!player.ip_hash || !player.campaign_id) {
            alert('Người chơi này không có thông tin IP hoặc chiến dịch để chặn.');
            return;
        }
        const label = `IP·${player.ip_hash.slice(0, 8)}`;
        if (!window.confirm(`Chặn ${label} (player: "${player.full_name || 'Ẩn danh'}") khỏi chiến dịch này?`)) return;

        setBlockingIpHash(player.ip_hash);
        try {
            const token = localStorage.getItem('auth_token');
            const API = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/$/, '');
            const res = await fetch(`${API}/admin/campaigns/${player.campaign_id}/block-ip`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    ip_hash: player.ip_hash,
                    label,
                    note: `Chặn từ trang Players — player: ${player.full_name || player.id}`,
                }),
            });
            const data = await res.json();
            if (!res.ok) {
                alert(data.message || 'Lỗi khi chặn IP.');
            } else {
                alert(`✅ Đã thêm ${label} vào danh sách chặn của chiến dịch.`);
                setPlayers(current => current.map(p => {
                    if (p.ip_hash === player.ip_hash) {
                        return { ...p, is_ip_blocked: true };
                    }
                    return p;
                }));
                if (detail && detail.ip_hash === player.ip_hash) {
                    setDetail(d => d ? { ...d, is_ip_blocked: true } : null);
                }
            }
        } catch {
            alert('Lỗi kết nối khi chặn IP.');
        } finally {
            setBlockingIpHash(null);
        }
    };

    // Unblock IP từ player row
    const unblockIpFromPlayer = async (player: Player | PlayerDetails) => {
        if (!player.ip_hash || !player.campaign_id) {
            alert('Người chơi này không có thông tin IP hoặc chiến dịch để bỏ chặn.');
            return;
        }
        const label = `IP·${player.ip_hash.slice(0, 8)}`;
        if (!window.confirm(`Bỏ chặn ${label} khỏi chiến dịch này?`)) return;

        setBlockingIpHash(player.ip_hash);
        try {
            const token = localStorage.getItem('auth_token');
            const API = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/$/, '');
            const res = await fetch(`${API}/admin/campaigns/${player.campaign_id}/block-ip`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    ip_hash: player.ip_hash,
                }),
            });
            const data = await res.json();
            if (!res.ok) {
                alert(data.message || 'Lỗi khi bỏ chặn IP.');
            } else {
                alert(`✅ Đã bỏ chặn ${label} thành công.`);
                setPlayers(current => current.map(p => {
                    if (p.ip_hash === player.ip_hash) {
                        return { ...p, is_ip_blocked: false };
                    }
                    return p;
                }));
                if (detail && detail.ip_hash === player.ip_hash) {
                    setDetail(d => d ? { ...d, is_ip_blocked: false } : null);
                }
            }
        } catch {
            alert('Lỗi kết nối khi bỏ chặn IP.');
        } finally {
            setBlockingIpHash(null);
        }
    };

    return (
        <Layout>
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900">Người chơi</h1>
                <p className="text-gray-500 mt-1">Quản lý lead, lịch sử quay, người trúng thưởng và trạng thái chặn.</p>
            </div>

            {error && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            <div className="mb-5 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <input
                        type="text"
                        value={filters.query}
                        onChange={(event) => updateFilter('query', event.target.value)}
                        placeholder="Tìm tên, email, SĐT, mã khách hàng"
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />

                    <select
                        value={filters.campaignId}
                        onChange={(event) => updateFilter('campaignId', event.target.value)}
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    >
                        <option value="">Tất cả chiến dịch</option>
                        {campaigns.map((campaign) => (
                            <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
                        ))}
                    </select>

                    <select
                        value={filters.rewardStatus}
                        onChange={(event) => updateFilter('rewardStatus', event.target.value as Filters['rewardStatus'])}
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    >
                        <option value="all">Tất cả kết quả</option>
                        <option value="winners">Đã trúng thưởng</option>
                        <option value="non_winners">Chưa trúng thưởng</option>
                    </select>

                    <select
                        value={filters.status}
                        onChange={(event) => updateFilter('status', event.target.value as Filters['status'])}
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    >
                        <option value="all">Tất cả trạng thái</option>
                        <option value="active">Hoạt động</option>
                        <option value="blocked">Đã chặn</option>
                    </select>

                    <select
                        value={filters.giftType}
                        onChange={(event) => updateFilter('giftType', event.target.value)}
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    >
                        <option value="">Tất cả loại quà</option>
                        {giftTypes.map((type) => (
                            <option key={type} value={type}>{getGiftTypeLabel(type)}</option>
                        ))}
                    </select>

                    <select
                        value={filters.giftId}
                        onChange={(event) => updateFilter('giftId', event.target.value)}
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    >
                        <option value="">Tất cả quà tặng</option>
                        {rewardGifts.map((gift) => (
                            <option key={gift.id} value={gift.id}>{gift.name}</option>
                        ))}
                    </select>

                    <input
                        type="date"
                        value={filters.startDate}
                        onChange={(event) => updateFilter('startDate', event.target.value)}
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />

                    <input
                        type="date"
                        value={filters.endDate}
                        onChange={(event) => updateFilter('endDate', event.target.value)}
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                    <button
                        onClick={searchPlayers}
                        className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                    >
                        Lọc dữ liệu
                    </button>
                    <button
                        onClick={resetFilters}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                        Xóa lọc
                    </button>
                    <div className="ml-auto text-sm text-gray-500">
                        {total.toLocaleString('vi-VN')} người chơi
                    </div>
                </div>
            </div>

            <div className="mb-3 flex flex-wrap items-center gap-2">
                <button
                    disabled={selectedIds.length === 0 || saving}
                    onClick={() => runBulkAction('block')}
                    className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                    Chặn
                </button>
                <button
                    disabled={selectedIds.length === 0 || saving}
                    onClick={() => runBulkAction('unblock')}
                    className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                    Bỏ chặn
                </button>
                <button
                    disabled={selectedIds.length === 0 || saving}
                    onClick={() => runBulkAction('delete')}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    Xóa khỏi danh sách
                </button>
                <span className="text-sm text-gray-500">
                    Đã chọn {selectedIds.length.toLocaleString('vi-VN')}
                </span>
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="w-10 px-4 py-3">
                                    <input
                                        type="checkbox"
                                        checked={allVisibleSelected}
                                        onChange={toggleAllVisible}
                                        aria-label="Chọn tất cả người chơi đang hiển thị"
                                    />
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Người chơi</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Chiến dịch</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Lượt / Trúng</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Quà đã trúng</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">IP</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Hoạt động gần nhất</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Trạng thái</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Hành động</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {loading ? (
                                <tr>
                                    <td colSpan={9} className="px-4 py-10 text-center text-sm text-gray-500">Đang tải...</td>
                                </tr>
                            ) : players.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="px-4 py-10 text-center text-sm text-gray-500">Không có người chơi phù hợp.</td>
                                </tr>
                            ) : players.map((player) => (
                                <tr key={player.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-4">
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(player.id)}
                                            onChange={() => togglePlayer(player.id)}
                                            aria-label={`Chọn ${player.full_name || player.identity_value || player.id}`}
                                        />
                                    </td>
                                    <td className="px-4 py-4">
                                        <div className="font-medium text-gray-900">{player.full_name || 'Khách chưa đặt tên'}</div>
                                        <div className="text-xs text-gray-500">{player.phone || player.email || player.identity_value || '-'}</div>
                                        {player.email && <div className="text-xs text-gray-400">{player.email}</div>}
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-700">{player.campaign_name || '-'}</td>
                                    <td className="px-4 py-4 text-sm">
                                        <span className="font-semibold text-gray-900">{player.total_plays || 0}</span>
                                        <span className="mx-1 text-gray-400">/</span>
                                        <span className="font-semibold text-emerald-600">{player.total_wins || 0}</span>
                                    </td>
                                    <td className="max-w-xs px-4 py-4 text-sm text-gray-600">
                                        {(player.won_gifts || []).length > 0 ? (
                                            <div className="flex flex-wrap gap-1">
                                                {player.won_gifts!.map((g, i) => (
                                                    <span key={i} className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-xs px-2 py-0.5 rounded-full border border-emerald-200 whitespace-nowrap">
                                                        🎁 {g.gift_name}{g.win_count > 1 ? <strong className="ml-0.5">×{g.win_count}</strong> : ''}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-gray-400">—</span>
                                        )}
                                    </td>
                                    {/* Cột IP */}
                                    <td className="px-4 py-4">
                                        {player.ip_hash ? (
                                            <div className="flex flex-col gap-1">
                                                <code className="font-mono text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded" title={player.ip_hash}>
                                                    {player.ip_hash.slice(0, 8)}…
                                                </code>
                                                {(player.shared_ip_count ?? 0) > 1 && (
                                                    <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-orange-700 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded-full">
                                                        ⚠️ {player.shared_ip_count} người
                                                    </span>
                                                )}
                                                {player.campaign_id && (
                                                    player.is_ip_blocked ? (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); unblockIpFromPlayer(player); }}
                                                            disabled={blockingIpHash === player.ip_hash}
                                                            title="Bỏ chặn IP này"
                                                            className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 px-1.5 py-0.5 rounded-full transition-colors disabled:opacity-50 shrink-0"
                                                        >
                                                            🔓 Unblock IP
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); blockIpFromPlayer(player); }}
                                                            disabled={blockingIpHash === player.ip_hash}
                                                            title="Chặn IP này khỏi chiến dịch"
                                                            className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 px-1.5 py-0.5 rounded-full transition-colors disabled:opacity-50 shrink-0"
                                                        >
                                                            🚫 Block IP
                                                        </button>
                                                    )
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-xs text-gray-300">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-600">{formatDate(player.last_played_at || player.last_seen_at)}</td>
                                    <td className="px-4 py-4">
                                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                                            player.status === 'blocked'
                                                ? 'bg-red-100 text-red-700'
                                                : 'bg-emerald-100 text-emerald-700'
                                        }`}>
                                            {getStatusLabel(player.status)}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4 text-sm">
                                        <button
                                            onClick={() => openDetails(player.id)}
                                            className="font-medium text-blue-600 hover:text-blue-800"
                                        >
                                            Chi tiết
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
                <button
                    disabled={page <= 1}
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    Trang trước
                </button>
                <div className="text-sm text-gray-500">Trang {page} / {totalPages}</div>
                <button
                    disabled={page >= totalPages}
                    onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    Trang sau
                </button>
            </div>

            {(detail || detailLoading) && (
                <div className="fixed inset-0 z-50 flex justify-end bg-black/30">
                    <div className="h-full w-full max-w-3xl overflow-y-auto bg-white p-6 shadow-xl">
                        <div className="mb-5 flex items-start justify-between gap-4">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900">Chi tiết người chơi</h2>
                                {detail && <p className="mt-1 text-sm text-gray-500">{detail.full_name || detail.identity_value || detail.id}</p>}
                            </div>
                            <button
                                onClick={() => setDetail(null)}
                                className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                                Đóng
                            </button>
                        </div>

                        {detailLoading ? (
                            <div className="py-20 text-center text-sm text-gray-500">Đang tải chi tiết...</div>
                        ) : detail && (
                            <>
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                    <div className="rounded-lg border border-gray-200 p-4">
                                        <div className="text-xs uppercase text-gray-500">Tổng lượt</div>
                                        <div className="mt-2 text-2xl font-bold text-gray-900">{detail.total_plays}</div>
                                    </div>
                                    <div className="rounded-lg border border-gray-200 p-4">
                                        <div className="text-xs uppercase text-gray-500">Đã trúng</div>
                                        <div className="mt-2 text-2xl font-bold text-emerald-600">{detail.total_wins}</div>
                                    </div>
                                    <div className="rounded-lg border border-gray-200 p-4">
                                        <div className="text-xs uppercase text-gray-500">Trạng thái</div>
                                        <div className="mt-2 text-lg font-semibold text-gray-900">{getStatusLabel(detail.status)}</div>
                                    </div>
                                </div>

                                <section className="mt-6">
                                    <h3 className="mb-3 text-lg font-semibold text-gray-900">Thông tin</h3>
                                    <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                                        <div><span className="text-gray-500">Chiến dịch:</span> {detail.campaign_name || '-'}</div>
                                        <div><span className="text-gray-500">SĐT:</span> {detail.phone || '-'}</div>
                                        <div><span className="text-gray-500">Email:</span> {detail.email || '-'}</div>
                                        <div><span className="text-gray-500">Mã khách hàng:</span> {detail.external_customer_id || '-'}</div>
                                        <div><span className="text-gray-500">Fingerprint:</span> {detail.fingerprint_hash ? <code className="text-xs bg-gray-100 px-1 rounded">{detail.fingerprint_hash.slice(0, 12)}…</code> : '-'}</div>
                                        <div>
                                            <span className="text-gray-500">IP Hash:</span>{' '}
                                            {detail.ip_hash ? (
                                                <span className="inline-flex items-center gap-2 flex-wrap">
                                                    <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-mono" title={detail.ip_hash}>
                                                        {detail.ip_hash.slice(0, 16)}…
                                                    </code>
                                                    {(detail.shared_ip_count ?? 0) > 1 && (
                                                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-orange-700 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-full">
                                                            ⚠️ IP cùng {detail.shared_ip_count} người
                                                        </span>
                                                    )}
                                                    {detail.campaign_id && (
                                                        detail.is_ip_blocked ? (
                                                            <button
                                                                onClick={() => unblockIpFromPlayer(detail)}
                                                                disabled={blockingIpHash === detail.ip_hash}
                                                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 px-2 py-0.5 rounded-full transition-colors disabled:opacity-50"
                                                            >
                                                                {blockingIpHash === detail.ip_hash ? '⏳…' : '🔓 Unblock IP'}
                                                            </button>
                                                        ) : (
                                                            <button
                                                                onClick={() => blockIpFromPlayer(detail)}
                                                                disabled={blockingIpHash === detail.ip_hash}
                                                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 px-2 py-0.5 rounded-full transition-colors disabled:opacity-50"
                                                            >
                                                                {blockingIpHash === detail.ip_hash ? '⏳…' : '🚫 Block IP'}
                                                            </button>
                                                        )
                                                    )}
                                                </span>
                                            ) : '-'}
                                        </div>
                                        <div><span className="text-gray-500">Lần đầu:</span> {formatDate(detail.first_seen_at)}</div>
                                        <div><span className="text-gray-500">Gần nhất:</span> {formatDate(detail.last_seen_at)}</div>
                                        <div><span className="text-gray-500">Lý do chặn:</span> {detail.blocked_reason || '-'}</div>
                                    </div>
                                </section>

                                <section className="mt-6">
                                    <h3 className="mb-3 text-lg font-semibold text-gray-900">Quà đã trúng</h3>
                                    {(detail.won_gifts || detail.rewards || []).length === 0 ? (
                                        <div className="rounded-lg border border-gray-100 bg-gray-50 p-4 text-sm text-gray-400 text-center">
                                            Chưa có quà trúng.
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {(detail.won_gifts || detail.rewards || []).map((item: any, idx: number) => (
                                                <div key={`gift-${idx}`} className="flex items-center justify-between rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-2.5">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-lg">🎁</span>
                                                        <span className="font-medium text-emerald-800 text-sm">
                                                            {item.gift_name || item.gift_name || '-'}
                                                        </span>
                                                    </div>
                                                    <span className="text-xs text-emerald-600">
                                                        {formatDate(item.won_at || item.assigned_at || item.created_at)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </section>

                                <section className="mt-6">
                                    <h3 className="mb-3 text-lg font-semibold text-gray-900">Lịch sử lượt chơi</h3>
                                    <div className="space-y-2">
                                        {(detail.play_sessions || []).length === 0 ? (
                                            <div className="rounded-lg border border-gray-200 p-4 text-sm text-gray-500">Chưa có lượt chơi.</div>
                                        ) : detail.play_sessions?.map((session: any, idx: number) => (
                                            <div key={`session-${session.id ?? idx}`} className="rounded-lg border border-gray-200 p-3 text-sm">
                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                    <div className="font-medium text-gray-900">{formatDate(session.started_at)}</div>
                                                    <div className="flex items-center gap-2">
                                                        {session.win && (
                                                            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                                                                🎁 {session.gift_name || 'Trúng thưởng'}
                                                            </span>
                                                        )}
                                                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                            session.status === 'finished'
                                                                ? 'bg-blue-50 text-blue-600'
                                                                : 'bg-gray-100 text-gray-500'
                                                        }`}>{session.status}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </section>

                            </>
                        )}
                    </div>
                </div>
            )}
        </Layout>
    );
}
